<template>
  <v-dialog :value="value" persistent>
    
    <v-card color="fill" v-resize="onResize" style='overflow:hidden'>
      <v-row>
        <v-col>
          <v-card-title :class="title">{{ad.ad_name}} Ad Planner</v-card-title>
        </v-col>
        <v-spacer />
        <v-col v-if="$auth.tenant === 'alliance-retail-group'" dense class="mr-0" cols="auto">
          <v-tooltip>
            <template #activator="{ on }">
              <v-btn
                v-on="on"
                @click="downloadPDF"
                :loading="loadingFile"
                :disabled="loadingFile"
                class="mt-2"
                text
                icon>
                <v-icon>mdi-file-download</v-icon>
              </v-btn>
            </template>
            <span>Download PDF</span>
          </v-tooltip>
        </v-col>
        <v-col v-if="$auth.tenant === 'awg' && ['Approved', 'Submitted', 'Draft'].includes(ad.status)" dense class="mr-0" cols="auto">
          <v-tooltip>
            <template #activator="{ on }">
              <div v-on="on" class="d-inline-block">
                <v-btn
                  :loading="loadingFile"
                  :disabled="loadingFile || ad.is_processing || ad.base_item_file_generated"
                  class="mt-2"
                  icon
                  @click="showConfirmModal = true">
                  <v-icon>mdi-file-download</v-icon>
                </v-btn>
              </div>
            </template>
            <span>{{ baseItemFileTooltip() }}</span>
          </v-tooltip>
        </v-col>
        <v-col dense cols="auto">
          <v-tooltip>
            <template #activator="{ on }">
              <v-btn
                v-on="on"
                @click="download"
                :loading="loadingFile"
                :disabled="loadingFile"
                class="mt-2"
                text
                icon>
                <v-icon>mdi-file-download-outline</v-icon>
              </v-btn>
            </template>
            <span>Download CSV</span>
          </v-tooltip>
        </v-col>
        <v-col class='mr-1 pr-4' dense cols="auto">
          <v-btn class="mt-2" text @click="$emit('update:value', false)" icon>
           <v-icon>mdi-close</v-icon>
          </v-btn>     
        </v-col>
      </v-row>
      <v-divider />
      <v-data-table 
      :headers="headers" 
      :height="tableHeight" 
      :items.sync="items" 
      class="mb-4"
      group-by='groupKey'
      no-data-text="No Data" 
      loading-text="Loading..."
      :loading="loading"
      style="cursor:pointer" 
      dense fixed-header hide-default-footer
      :items-per-page="pageSize"
      :expanded="expanded">

      <template v-slot:[`group.header`]="{ group, headers, toggle, isOpen, items }">
        <td :colspan="headers.length">
                <v-btn dense @click="toggle" small icon :ref="group" :data-open="isOpen">
                    <v-icon v-if="isOpen">mdi-chevron-up</v-icon>
                    <v-icon v-else>mdi-chevron-down</v-icon>
                </v-btn>
                {{ items[0].groupName }} 
            </td>
        </template>

       <!-- formatting the headers -->
        <template #[`item.case_cost`]="{ item }">
          <span v-if="item.case_cost">${{formatCurrencyDisplay(item.case_cost)}}</span>
          <span v-else> - </span>
        </template>

        <template #[`item.department`]="{ item }">
          <span v-if="item.department">{{item.department}}</span>
          <span v-else> - </span>
        </template>

        <template #[`item.feature_text`]="{ item }">
          <span v-if="item.feature_text">{{item.feature_text}}</span>
          <span v-else> - </span>
        </template>

        <template #[`item.net_case_cost`]="{ item }">
          <span v-if="item.net_case_cost">${{formatCurrencyDisplay(item.net_case_cost)}}</span>
          <span v-else> - </span>
        </template>

        <template #[`item.pcaf_billback`]="{ item }">
          <span v-if="item.pcaf_billback">${{formatCurrencyDisplay(item.pcaf_billback)}}</span>
          <span v-else> - </span>
        </template>

        <template #[`item.amap`]="{ item }">
          <span v-if="item.amap">  ${{formatCurrencyDisplay(item.amap)}} </span>
          <span v-else> - </span>
        </template>

        <template #[`item.early_buy_billback`]="{ item }">
          <span v-if="item.early_buy_billback">  ${{formatCurrencyDisplay(item.early_buy_billback)}} </span>
          <span v-else> - </span>
        </template>

        <template #[`item.ad_billback`]="{ item }">
        <span v-if="item.ad_billback">  
          ${{formatCurrencyDisplay(item.ad_billback)}} </span>
          <span v-else> - </span>
        </template>

        <template #[`item.edlc_billback`]="{ item }">
        <span v-if="item.edlc_billback">  
          ${{formatCurrencyDisplay(item.edlc_billback)}} </span>
          <span v-else> - </span>
        </template>

        <template #[`item.tpr_billback`]="{ item }">
          <span v-if="item.tpr_billback">  ${{formatCurrencyDisplay(item.tpr_billback)}} </span>
          <span v-else> - </span>
        </template>
        
        <template #[`item.dsd_oi_billback`]="{ item }">
          <span v-if="item.dsd_oi_billback">  ${{formatCurrencyDisplay(item.dsd_oi_billback)}} </span>
          <span v-else> - </span>
        </template>

        <template #[`item.ad_scan`]="{ item }">
          <span v-if="item.ad_scan">  ${{formatCurrencyDisplay(item.ad_scan)}} </span>
          <span v-else> - </span>
        </template>

        <template #[`item.edlc_scan`]="{ item }">
          <span v-if="item.edlc_scan">  ${{formatCurrencyDisplay(item.edlc_scan)}} </span>
          <span v-else> - </span>
        </template>

        <template #[`item.complex_scan`]="{ item }">
           <span v-if="item.complex_scan">  ${{formatCurrencyDisplay(item.complex_scan)}} </span>
          <span v-else> - </span>
        </template>

        <template #[`item.tpr_scan`]="{ item }">
           <span v-if="item.tpr_scan">  ${{formatCurrencyDisplay(item.tpr_scan)}} </span>
          <span v-else> - </span>
        </template>
        
        <template #[`item.epay_edlc_scan`]="{ item }">
           <span v-if="item.epay_edlc_scan">  ${{formatCurrencyDisplay(item.epay_edlc_scan)}} </span>
          <span v-else> - </span>
        </template>

        <template #[`item.epay_tpr_scan`]="{ item }">
           <span v-if="item.epay_tpr_scan">  ${{formatCurrencyDisplay(item.epay_tpr_scan)}} </span>
          <span v-else> - </span>
        </template>

        <template #[`item.epay_ad_scan`]="{ item }">
           <span v-if="item.epay_ad_scan">  ${{formatCurrencyDisplay(item.epay_ad_scan)}} </span>
          <span v-else> - </span>
        </template>

        <template #[`item.edlc_scan_compete`]="{ item }">
           <span v-if="item.edlc_scan_compete">  ${{formatCurrencyDisplay(item.edlc_scan_compete)}} </span>
          <span v-else> - </span>
        </template>

        <template #[`item.tpr_scan_compete`]="{ item }">
           <span v-if="item.tpr_scan_compete">  ${{formatCurrencyDisplay(item.tpr_scan_compete)}} </span>
          <span v-else> - </span>
        </template>

        <template #[`item.regular_retail`]="{ item }">
          <span v-if="item.regular_retail">  ${{formatCurrencyDisplay(item.regular_retail)}} </span>
          <span v-else> - </span>
        </template>

        <template #[`item.tpr_count`]="{ item }">
          <span v-if="item.tpr_count">  {{item.tpr_count}} </span>
          <span v-else> - </span>
        </template>

        <template #[`item.tpr_retail`]="{ item }">
          <span v-if="item.tpr_retail">  ${{formatCurrencyDisplay(item.tpr_retail)}} </span>
          <span v-else> - </span>
        </template>

        <template #[`item.tpr_dates`]="{ item }">
          <span v-if="item.tpr_start_date">  {{item.tpr_start_date}} - {{item.tpr_end_date}} </span>
          <span v-else> - </span>
        </template>

        <template #[`item.unit_cost`]="{ item }">
          <span v-if="item.unit_cost">  ${{formatCurrencyDisplay(item.net_unit_cost)}} </span>
          <span v-else> - </span>
        </template>

        <template #[`item.complex_net_unit_cost`]="{ item }">
           <span v-if="item.complex_net_unit_cost">  ${{formatCurrencyDisplay(item.complex_net_unit_cost)}} </span>
          <span v-else> - </span>
        </template>

        <template #[`item.ad_retail`]="{ item }">
          <span v-if="item.ad_retail">  ${{formatCurrencyDisplay(item.ad_retail)}} </span>
          <span v-else> - </span>
        </template>
        
        <template #[`item.qty_per_case`]="{ item }">
            <span v-if="item.qty_per_case">  {{item.qty_per_case}} </span>
          <span v-else> - </span>
        </template>

        <template #[`item.last_90_days_shipped_qty`]="{ item }">
            <span v-if="item.last_90_days_shipped_qty">  {{item.last_90_days_shipped_qty}} </span>
          <span v-else> - </span>
        </template>

        <template #[`item.ad_gross_profit_percent`]="{ item }">
            <span v-if="item.ad_gross_profit_percent">  {{gp_percent_display(item)}}% </span>
          <span v-else> - </span>
        </template>

        <template #[`item.enforce_compliance`]="{ item }">
          <v-icon v-if="item.enforce_compliance"> mdi-shield-check-outline </v-icon>
          <span v-else> - </span>
        </template>

        <template #[`item.sign_offer_details.must_buy_amount`]="{ item }">
            <span v-if="item.sign_offer_details.must_buy_amount && item.buy_item === true">  {{item.sign_offer_details.must_buy_amount}} </span>
          <span v-else> - </span>
        </template>

        <template #[`item.sign_offer_details.percent_off`]="{ item }">
            <span v-if="item.sign_offer_details.percent_off">  {{item.sign_offer_details.percent_off}}% </span>
          <span v-else> - </span>
        </template>

        <template #[`item.get_item`]="{ item }">
          <v-icon v-if="item.get_item">mdi-check-bold</v-icon>
          <span v-else> - </span>
        </template>

        <template #[`item.sign_offer_details.get_item_amount`]="{ item }">
            <span v-if="item.sign_offer_details.get_item_amount && item.get_item === true">  {{item.sign_offer_details.get_item_amount}} </span>
          <span v-else> - </span>
        </template>
        
        <template #[`item.sign_offer_details.get_item_retail`]="{ item }">
            <span v-if="item.sign_offer_details.get_item_retail && item.get_item === true">  ${{formatCurrencyDisplay(item.sign_offer_details.get_item_retail)}} </span>
          <span v-else> - </span>
        </template>

        <template #[`item.components`]="{ item }">
        <v-tooltip top v-if="item.is_shipper">
          <template #activator="{ on }">
            <v-btn v-on="on"
              icon color="primary" @click="selectedItem=item, showComponents = true">
              <v-icon>mdi-view-list</v-icon>
            </v-btn>
          </template>
          <span>Components</span>
        </v-tooltip>
      </template>

      </v-data-table>
      <v-toolbar dense flat color="#fafafa" class='mt-0'>
      <v-row align="center" justify="end" class='mt-0 pt-0'>
        <v-col cols="2" style="max-width:150px;">
          <v-select class="mt-5" dense :items="pageSizes" label="Items Per Page" select v-model="pageSize"
            :menu-props="{ top: true, offsetY: true, maxHeight: 500 }" @change='getItems()'>
          </v-select>
        </v-col>
        <v-btn icon dense @click="previous()" :disabled="disablePrevious" class="mx-2">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <small>Page {{page}}</small>
        <v-btn icon dense @click="next()" :disabled="disableNext" class="mx-2">
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </v-row>
    </v-toolbar>

    <ComponentItems :value.sync="showComponents" :selectedItem="selectedItem" :selectedAd="ad" />
    </v-card>
    <v-dialog justify="center" :value="showConfirmModal" persistent max-width="450">
      <v-card color=#fafafa>
        <section class="ma-0 pa-6">
          <v-row justify="center">
            <v-icon x-large>mdi-file-download</v-icon>
          </v-row>
          <v-row justify="center">
            <v-col cols="12" class="text-center">
              <h3>
                Generate the Base AdplanItem File?
              </h3>
              <span>Once this has been generated for this Ad, it cannot be generated again.</span>
            </v-col>
          </v-row>

          <v-row justify="center">
            <v-btn height="40" class="elevation-1" @click.stop="showConfirmModal = false">
              No
            </v-btn>
            <v-btn color="#0c67a5" height="40" class="white--text ml-2"
              @click="triggerBaseAdplanItemFile">Yes
            </v-btn>
          </v-row>
        </section>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import { displayHelpers } from '@/mixins/display-helpers'
import { format } from '@/mixins/format'

const ComponentItems = () => import('@/components/ads/ComponentItems.vue')

export default {
  name: 'AdPlannerView',
  mixins: [displayHelpers, format],
  components: { ComponentItems },
  props: [
    'value',
    'selectedAd'
  ],
  data () {
    return {
      ad: {},
      items: [],
      search: '',
      tableHeight:'',
      expanded: [],
      sortBy:'page_name',
      sortDesc:true,
      offset: true,
      showComponents: false,
      selectedItem: {},
      page: 1,
      pageSize: 50,
      pageSizes: [50, 100],
      loading: false,
      isDocumentThere: false,
      loadingFile: false,
      doc: {},
      link: {},
      showConfirmModal: false
    }
  },
  computed: {
    disableNext () {
      return this.items.length < this.pageSize
    },
    disablePrevious () {
      return (this.page - 1) * this.pageSize === 0
    },
    headers () {
      if (this.$auth.tenant === 'pricechopper') {
          return [
          { sortable: false, class: 'accent white--text', width: '3%', show: true},
          { text: 'PCE Link Code', align: 'left', sortable: false, filterable: true, value: 'pce_link_code', class: 'accent white--text', width: '4%'},
          { text: 'Dept', align: 'left', sortable: false, filterable: true, value: 'department', class: 'accent white--text', width: '10%' },
          { text: 'Feature Id', align: 'left', sortable: false, filterable: true, value: 'feature_id', class: 'accent white--text', width: '10%' },
          { text: 'Feature Desc', align: 'left', sortable: false, filterable: true, value: 'feature_text', class: 'accent white--text' },
          { text: 'Item Code', align: 'left', sortable: false, filterable: true, value: 'item_id', class: 'accent white--text', width: '6%'},
          { text: 'GTIN', align: 'left', sortable: false, filterable: true, value: 'gtin', class: 'accent white--text', width: '2%'},
          { text: 'UPC', align: 'left', sortable: false, filterable: true, value: 'upc', class: 'accent white--text', width: '2%'},
          { text: 'Item Desc', align: 'left', sortable: false, filterable: true, value: 'item_desc', class: 'accent white--text'},
          { text: 'Quantity Per Case', align: 'left', sortable: false, filterable: true, value: 'qty_per_case', class: 'accent white--text'},
          { text: 'Pack Size', align: 'left', sortable: false, filterable: true, value: 'pack_size', class: 'accent white--text', width: '6%'},
          { text: 'Case Cost', align: 'left', sortable: false, filterable: true, value: 'case_cost', class: 'accent white--text'},
          { text: 'AMAP', align: 'left', sortable: false, filterable: true, value: 'amap', class: 'accent white--text'},
          { text: 'EBA', align: 'left', sortable: false, filterable: true, value: 'early_buy_billback', class: 'accent white--text'},
          { text: 'EDLC BB', align: 'left', sortable: false, filterable: true, value: 'edlc_billback', class: 'accent white--text'},
          { text: 'Ad BB', align: 'left', sortable: false, filterable: true, value: 'ad_billback', class: 'accent white--text'},
          { text: 'TPR BB', align: 'left', sortable: false, filterable: true, value: 'tpr_billback', class: 'accent white--text'},
          { text: 'DSD OI', align: 'left', sortable: false, filterable: true, value: 'dsd_oi_billback', class: 'accent white--text'},
          { text: 'EDLC Scan', align: 'left', sortable: false, filterable: true, value: 'edlc_scan', class: 'accent white--text'},
          { text: 'Ad Scan', align: 'left', sortable: false, filterable: true, value: 'ad_scan', class: 'accent white--text'},
          { text: 'Complex Redemp.', align: 'left', sortable: false, filterable: true, value: 'complex_scan', class: 'accent white--text'},
          { text: 'TPR Scan', align: 'left', sortable: false, filterable: true, value: 'tpr_scan', class: 'accent white--text'},
          { text: 'Net Unit Cost', align: 'left', sortable: false, filterable: true, value: 'unit_cost', class: 'accent white--text'},
          { text: 'NUC - Complex', align: 'left', sortable: false, filterable: true, value: 'complex_net_unit_cost', class: 'accent white--text'},
          { text: 'Gross Profit', align: 'left', sortable: false, filterable: true, value: 'ad_gross_profit_percent', class: 'accent white--text', width: '8%',},
          { text: 'Count', align: 'left', sortable: false, filterable: true, value: 'ad_retail_count', class: 'accent white--text'},
          { text: 'Ad Retail', align: 'left', sortable: false, filterable: true, value: 'ad_retail', class: 'accent white--text'},
          { text: 'Regular Retail', align: 'left', sortable: false, filterable: true, value: 'regular_retail', class: 'accent white--text'},
          { text: 'Mvmt', align: 'left', sortable: false, filterable: true, value: 'last_90_days_shipped_qty', class: 'accent white--text', width: '10%'},
          { sortable: false, filterable: true, value: 'components', class: 'accent white--text', width: '10%'},
          { sortable: false, class: 'accent white--text', width: '12%'}
        ]
      } else if (this.$auth.tenant === 'alliance-retail-group') {
          return [
          { sortable: false, class: 'accent white--text', width: '3%'},
          { text: 'Item Code', align: 'left', sortable: false, filterable: true, value: 'item_id', class: 'accent white--text', width: '10%' },
          { text: 'Dept', align: 'left', sortable: false, filterable: true, value: 'department', class: 'accent white--text' },
          { text: 'Feature Desc.', align: 'left', sortable: false, filterable: true, value: 'feature_text', class: 'accent white--text' },
          { text: 'UPC', align: 'left', sortable: false, filterable: true, value: 'upc', class: 'accent white--text', width: '10%' },
          { text: 'Item Desc.', align: 'left', sortable: false, filterable: true, value: 'item_desc', class: 'accent white--text' },
          { text: 'Pack', align: 'left', sortable: false, filterable: true, value: 'qty_per_case', class: 'accent white--text' },
          { text: 'Size', align: 'left', sortable: false, filterable: true, value: 'pack_size', class: 'accent white--text', width: '6%' },
          { text: 'Case Cost', align: 'left', sortable: false, filterable: true, value: 'case_cost', class: 'accent white--text' },
          { text: 'Cost+Freight', align: 'left', sortable: false, filterable: true, value: 'net_case_cost', class: 'accent white--text' },
          { text: 'AMAP', align: 'left', sortable: false, filterable: true, value: 'amap', class: 'accent white--text'},
          { text: 'EBA', align: 'left', sortable: false, filterable: true, value: 'early_buy_billback', class: 'accent white--text'},
          { text: 'DSD OI', align: 'left', sortable: false, filterable: true, value: 'dsd_oi_billback', class: 'accent white--text'},
          { text: 'EDLC BB', align: 'left', sortable: false, filterable: true, value: 'edlc_billback', class: 'accent white--text'},
          { text: 'TPR BB', align: 'left', sortable: false, filterable: true, value: 'tpr_billback', class: 'accent white--text'},
          { text: 'Ad BB', align: 'left', sortable: false, filterable: true, value: 'ad_billback', class: 'accent white--text'},
          { text: 'EDLC Scan', align: 'left', sortable: false, filterable: true, value: 'edlc_scan', class: 'accent white--text'},
          { text: 'EDLC ePay', align: 'left', sortable: false, filterable: true, value: 'epay_edlc_scan', class: 'accent white--text'},
          { text: 'EDLC Compete', align: 'left', sortable: false, filterable: true, value: 'edlc_scan_compete', class: 'accent white--text'},
          { text: 'TPR Scan', align: 'left', sortable: false, filterable: true, value: 'tpr_scan', class: 'accent white--text'},
          { text: 'TPR ePay', align: 'left', sortable: false, filterable: true, value: 'epay_tpr_scan', class: 'accent white--text'},
          { text: 'TPR Compete', align: 'left', sortable: false, filterable: true, value: 'tpr_scan_compete', class: 'accent white--text'},
          { text: 'Ad Scan', align: 'left', sortable: false, filterable: true, value: 'ad_scan', class: 'accent white--text'},
          { text: 'Ad Scan ePay', align: 'left', sortable: false, filterable: true, value: 'epay_ad_scan', class: 'accent white--text'},
          { text: 'Complex Redemp.', align: 'left', sortable: false, filterable: true, value: 'complex_scan', class: 'accent white--text'},
          { text: 'Net Unit Cost', align: 'left', sortable: false, filterable: true, value: 'unit_cost', class: 'accent white--text' },
          { text: 'NUC - Complex', align: 'left', sortable: false, filterable: true, value: 'complex_net_unit_cost', class: 'accent white--text'},
          { text: 'Per Case Ad Fee', align: 'left', sortable: false, filterable: true, value: 'pcaf_billback', class: 'accent white--text' },
          { text: 'Ad Count', align: 'left', sortable: false, filterable: true, value: 'ad_retail_count', class: 'accent white--text' },
          { text: 'Ad Retail', align: 'left', sortable: false, filterable: true, value: 'ad_retail', class: 'accent white--text' },
          { text: 'Gross Profit', align: 'left', sortable: false, filterable: true, value: 'ad_gross_profit_percent', class: 'accent white--text', width: '10%' },
          { text: 'Regular Retail', align: 'left', sortable: false, filterable: true, value: 'regular_retail', class: 'accent white--text' },
          { text: 'Mvmt', align: 'left', sortable: false, filterable: true, value: 'last_90_days_shipped_qty', class: 'accent white--text', width: '10%' },
          { text: 'TPR Retail', align: 'left', sortable: false, filterable: true, value: 'tpr_retail', class: 'accent white--text', width: '15%' },
          { text: 'TPR Dates', align: 'left', sortable: false, filterable: true, value: 'tpr_dates', class: 'accent white--text', width: '15%' },
          { sortable: false, filterable: true, value: 'components', class: 'accent white--text', width: '10%' },
          { sortable: false, class: 'accent white--text', width: '12%'}
        ]
      } else {
        return [
          { sortable: false, class: 'accent white--text', width: '3%'},
          { text: 'Item Code', align: 'left', sortable: false, filterable: true, value: 'item_id', class: 'accent white--text', width: '10%' },
          { text: 'Feature/Description', align: 'left', sortable: false, filterable: true, value: 'item_desc', class: 'accent white--text' },
          { text: 'Quantity Per Case', align: 'left', sortable: false, filterable: true, value: 'qty_per_case', class: 'accent white--text' },
          { text: 'Pack Size', align: 'left', sortable: false, filterable: true, value: 'pack_size', class: 'accent white--text', width: '6%' },
          { text: 'Case Cost', align: 'left', sortable: false, filterable: true, value: 'case_cost', class: 'accent white--text' },
          { text: 'AMAP', align: 'left', sortable: false, filterable: true, value: 'amap', class: 'accent white--text' },
          { text: 'EBA', align: 'left', sortable: false, filterable: true, value: 'early_buy_billback', class: 'accent white--text' },
          { text: 'EDLC BB', align: 'left', sortable: false, filterable: true, value: 'edlc_billback', class: 'accent white--text' },
          { text: 'Ad BB', align: 'left', sortable: false, filterable: true, value: 'ad_billback', class: 'accent white--text' },
          { text: 'TPR BB', align: 'left', sortable: false, filterable: true, value: 'tpr_billback', class: 'accent white--text' },
          { text: 'EDLC Scan', align: 'left', sortable: false, filterable: true, value: 'edlc_scan', class: 'accent white--text' },
          { text: 'Ad Scan', align: 'left', sortable: false, filterable: true, value: 'ad_scan', class: 'accent white--text' },
          { text: 'TPR Scan', align: 'left', sortable: false, filterable: true, value: 'tpr_scan', class: 'accent white--text' },
          { text: 'Net Unit Cost', align: 'left', sortable: false, filterable: true, value: 'unit_cost', class: 'accent white--text' },
          { text: 'Gross Profit', align: 'left', sortable: false, filterable: true, value: 'ad_gross_profit_percent', class: 'accent white--text', width: '10%' },
          ...(this.$auth.tenant === 'awg' ? [{ text: 'Enforce Compliance', align: 'left', sortable: false, filterable: true, value: 'enforce_compliance', class: 'accent white--text', width: '10%' }] : []),
          { text: 'Count', align: 'left', sortable: false, filterable: true, value: 'ad_retail_count', class: 'accent white--text' },
          { text: 'Ad Retail', align: 'left', sortable: false, filterable: true, value: 'ad_retail', class: 'accent white--text' },
          { text: 'Regular Retail', align: 'left', sortable: false, filterable: true, value: 'regular_retail', class: 'accent white--text' },
          { text: 'UPC', align: 'left', sortable: false, filterable: true, value: 'upc', class: 'accent white--text', width: '10%' },
          { text: 'Mvmt', align: 'left', sortable: false, filterable: true, value: 'last_90_days_shipped_qty', class: 'accent white--text', width: '10%' },
          { text: 'Offer Type', align: 'left', sortable: false, filterable: true, value: 'sign_offer_details.sign_offer_type', class: 'accent white--text', cellClass: 'text-no-wrap' },
          { text: 'Must Buy Units', align: 'left', sortable: false, filterable: true, value: 'sign_offer_details.must_buy_amount', class: 'accent white--text', width: '10%' },
          { text: '% Off', align: 'left', sortable: false, filterable: true, value: 'sign_offer_details.percent_off', class: 'accent white--text', width: '10%' },
          { text: 'Get Item', align: 'left', sortable: false, filterable: true, value: 'get_item', class: 'accent white--text', width: '10%' },
          { text: 'Get Count', align: 'left', sortable: false, filterable: true, value: 'sign_offer_details.get_item_amount', class: 'accent white--text', width: '10%' },
          { text: 'Get Ad Retail', align: 'left', sortable: false, filterable: true, value: 'sign_offer_details.get_item_retail', class: 'accent white--text', width: '10%' },
          { sortable: false, filterable: true, value: 'components', class: 'accent white--text', width: '10%' },
          { sortable: false, class: 'accent white--text', width: '12%'}
        ]
      }
    },
  },

  async created () {
    this.tableHeight =  window.innerHeight - 215
  },
  
  watch: {
    value: {
      immediate: true,
      async handler () {
        if (this.value){
          // use the selectedAd prop to hit the API call to get accurate ad data
          await this.getAdData()
          await this.getItems()
          this.searchDocs()
        }
      }
    }
  },

  methods: {
    async download () {
      if (this.ad.status === 'Approved') {
        if (!this.ad.ad_planner_url) {
          await this.searchDocs()
        }
        return location.href = this.ad.ad_planner_url
      }
      this.loadingFile = true
      try {
        const res = await this.$Ads.generateCSV(this.ad.id)
        if (res?.data) {
          location.href = res.data
        }
      } catch (err) {
        console.error(err)
        this.$store.dispatch('setSnackbar', { status: 'error', text: `Error ${err?.response?.data?.message || err.message}` })
      } finally {
        this.loadingFile = false
      }
    },

    async downloadPDF () {
      if (this.ad.status === 'Approved') {
        const url = await this.searchDocs('pdf')
        if (url) window.open(url, '_blank')
        return
      }
      this.loadingFile = true
      try {
        const res = await this.$Ads.downloadPDF(this.ad.id)
        if (res?.data) {
          window.open(res.data, '_blank');
        }
      } catch (err) {
        console.error(err)
        this.$store.dispatch('setSnackbar', { status: 'error', text: `Error ${err?.response?.data?.message || err.message}` })
      } finally {
        this.loadingFile = false
      }
    },

    async triggerBaseAdplanItemFile () {
      this.loadingFile = true
      this.showConfirmModal = false
      try {
        const AdData = {
          "id": this.ad.id, 
          "ad_party_id": this.ad.ad_party_id,
          "ad_start_date": this.ad.ad_start_date,
          "ad_end_date": this.ad.ad_end_date,
          "hosting": "False",
          "pangea": "False",
          "connect": "True"
        }
        
        const { data } = await this.$Ads.generateSystematicTxt(AdData)
        if (data.status === 'submitted') {
          this.$store.dispatch('setSnackbar', { status: 'success', text: `Base AdPlanItem file generation has been triggered` })
          this.$emit('update:value', false)
        } else {
          throw Error("Unable to trigger Base Adplanitem file generation")
        }
      } catch (err) {
        console.error(err)
        this.$store.dispatch('setSnackbar', { status: 'error', text: `Error ${err?.response?.data?.message || err.message}` })
      } finally {
        this.loadingFile = false
      }
    },

    async getItems () {
      const limit = this.pageSize
      const offset = (this.page - 1) * this.pageSize
      const party = this.ad.ad_party_id
      const params = {
        party_id: party,
        limit, 
        offset
      }
      let items = []
      this.loading = true
      try {
        const res = await this.$AdRetails.getItems(this.ad.id, params)
        if (res?.data?.length > 0) {
          items = res.data.map(item => {
            item.groupName = `${item.page_name} : ${item.position_name}`
            item.groupKey = `${item.page_display_order.toString().padStart(6, '0')}${item.position_display_order.toString().padStart(8, '0')}`
            return item
          })
        }
        this.items = items
      } catch (err) {
        this.$store.dispatch('setSnackbar', { status: 'error', text: `Failed to Get Items due to ${err?.response?.data?.message || err.message}` })
      } finally {
        this.loading = false
      }
    },

    onResize () {
      this.tableHeight =  window.innerHeight - 240
    },

    async searchDocs(export_type = 'csv') {
      if (this.ad.status == 'Approved') {
        this.loadingFile = true
        const data = { party_ids: [this.ad.ad_party_id] }
        let tags = { ad_id: this.ad.id }
        if (this.$auth.tenant === 'alliance-retail-group') {
          tags = { 
            ...tags, 
            category: 'RETAILER_REPORT',
            subcategory: 'RETAILER_AD_PLANNING',
            subtype: 'AD_PLANNER',
            export_type: export_type 
          }
        }
        try {
          const res = await this.$Documents.searchDocs(tags, data)
          if (res?.data?.length > 0) {
            const doc = res.data.find(r => r.tags?.ad_id === this.ad.id)
            if (doc) {
              const fileRes = await this.$Documents.getFile(doc.id)
              if (fileRes?.data?.url) {
                if (export_type === 'pdf') {
                  return fileRes.data.url
                }
                this.ad.ad_planner_url = fileRes.data.url
              }
            }
          }
        } catch (err) {
          console.error(err)
          this.$store.dispatch('setSnackbar', { status: 'error', text: `Error ${err?.response?.data?.message || err.message}` })
        } finally {
          this.loadingFile = false
        }
      }
    },

    previous () {
      this.page = this.page - 1
      if (this.page < 1) {
        this.page = 1
      }
      this.getItems()
    },

    next () {
      this.page = this.page + 1
      this.getItems()
    },

    gp_percent_display(item) {
      const gp_value = item.ad_gross_profit_percent

      if (this.$auth.tenant !== 'awg')
        return this.formatCurrencyDisplay(gp_value)

      if (item.get_item === true && item.sign_offer_details?.sign_offer_type == 'Buy X Get Y')
        return '0.00'

      return this.formatCurrencyDisplay(gp_value)
    },

    async getAdData() {
      this.loading = true
      try {
        const { data } = await this.$Ads.getAdById(this.selectedAd.id)
        this.ad = data
      } catch (err) {
        this.$store.dispatch('setSnackbar', { status: 'error', text: `Failed to Get Ad Data due to ${err?.response?.data?.message || err.message}` })
      } finally {
        this.loading = false
      }
    },

    baseItemFileTooltip() {
      if (this.ad.base_item_file_generated) {
        return "The Base AdplanItem File has already been generated for this Ad"
      } else if (this.ad.is_processing) {
        return "Cannot trigger while Ad is Processing"
      } else {
        return "Trigger Base Adplanitem file creation"
      }
    }
  }
}
</script>
